import addMinutes from "date-fns/addMinutes";
import { DISPLAY_DATE } from "@constants/date-formats";
import { resources } from "@constants/hardcoded-strings";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { FormattedAdaptiveAuthenticationResult } from "@idw-types/data/adaptive-authentication-result/adaptive-authentication-result";
import { formatDateToString, sortDates } from "@utils/date-util";
import { IdwTable } from "@containers/common/idw-table/idw-table";
import { PaginationContainer } from "@containers/common/pagination-container/pagination-container";
import { IdwTableContainer } from "@containers/common/idw-table-container/idw-table-container";
import { createColumnHelper, getCoreRowModel, getPaginationRowModel, getSortedRowModel, TableState, useReactTable } from "@tanstack/react-table";
import { useDispatch } from "react-redux";
import { useMemo } from "react";
import { handleSetPaginationChange, handleSetSortingChange } from "@utils/react-table/react-table-util";

type AdaptiveAuthenticationTableContainerProps = {
    adaptiveAuthenticationResults: Array<FormattedAdaptiveAuthenticationResult>;
    tableState: TableState;
};

export const AdaptiveAuthenticationTableContainer = (props: AdaptiveAuthenticationTableContainerProps) => {
    const currentLocalDate = new Date();
    const timeZoneOffset = currentLocalDate.getTimezoneOffset();
    const { adaptiveAuthenticationResults, tableState } = props;
    const dispatch = useDispatch();

    const columnHelper = createColumnHelper<FormattedAdaptiveAuthenticationResult>();

    const columns = useMemo(() => ([
        columnHelper.accessor("date", {
            header: resources.tables.adaptiveAuthentication.columns.date.header,
            cell: (props) => {
                if (props.getValue()) {
                    const utcDate = addMinutes(props.getValue(), timeZoneOffset);
                    return <span>{formatDateToString(utcDate, DISPLAY_DATE)}</span>;
                }
                return null;
            },
            sortingFn: (a, b) => sortDates(a.original.date, b.original.date)
        }),
        columnHelper.accessor("uniqueUsers", {
            header: resources.tables.adaptiveAuthentication.columns.uniqueUsers.header
        }),
        columnHelper.accessor("totalLogins", {
            header: resources.tables.adaptiveAuthentication.columns.totalLogins.header
        }),
        columnHelper.accessor("mfaSuppressed", {
            header: resources.tables.adaptiveAuthentication.columns.mfaSuppressed.header
        }),
        columnHelper.accessor("mfaNewDevicesTrusted", {
            header: resources.tables.adaptiveAuthentication.columns.mfaNewDevicesTrusted.header
        }),
        columnHelper.accessor("mfaProfilingFailed", {
            header: resources.tables.adaptiveAuthentication.columns.mfaProfilingFailed.header
        }),
        columnHelper.accessor("loginDenied", {
            header: resources.tables.adaptiveAuthentication.columns.loginDenied.header
        })
    ]), [adaptiveAuthenticationResults]);

    const useTableResult = useReactTable({
        columns,
        data: adaptiveAuthenticationResults,
        state: tableState,
        filterFns: null,
        sortDescFirst: false,
        autoResetPageIndex: true,
        onSortingChange: (sortingOrUpdateFn) => handleSetSortingChange(sortingOrUpdateFn, TableSettingsTableTypes.ADAPTIVE_AUTHENTICATION, tableState.sorting, dispatch),
        onPaginationChange: (paginationOrUpdateFn) => handleSetPaginationChange(paginationOrUpdateFn, TableSettingsTableTypes.ADAPTIVE_AUTHENTICATION, tableState.pagination, dispatch),
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return (
        <IdwTableContainer>
            <IdwTable
                id="adaptive-authentication"
                useTableResult={useTableResult}
                headerColumnKeyPrefix={"adaptive-authentication"}
            />
            <PaginationContainer
                idPrefix="adaptive-authentication"
                useTableResult={useTableResult}
            />
        </IdwTableContainer>
    );
};